/**
 * Loads the Google Maps JS SDK on demand, once.
 *
 * <p>The SDK was previously a plain `<script async defer>` in index.html, so
 * every page paid for it whether or not it drew a map — and it is not a small
 * tax: 1,326 KB, measured, which is more than two and a half times the entire
 * application bundle and the single largest resource the site serves. The home
 * page needs it only if a visitor actually clicks into the location search, and
 * most visitors never do.
 *
 * <p>Every consumer here already polls `window.google` on an interval before
 * using it — they were written for an async script tag that might not have
 * landed yet — so injecting the script later slots into the behaviour they
 * already have, rather than requiring each of them to be rewritten.
 *
 * <p>Callers should invoke this as late as they honestly can: on focus of a
 * search field, or on mount of a screen that draws a map immediately.
 */

const KEY = 'AIzaSyDgB7L2-SrZS7qDUN7EdzsGryqZCeSDzqI';
const SRC = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places`;

let promise = null;

const ensureMapsLoaded = () => {
    if (promise) return promise;

    // Already present — either a previous call, or a page that still hardcodes it.
    if (typeof window !== 'undefined' && window.google?.maps) {
        promise = Promise.resolve(window.google);
        return promise;
    }

    promise = new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
            reject(new Error('no document'));
            return;
        }
        const existing = document.querySelector(`script[data-google-maps]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(window.google));
            existing.addEventListener('error', reject);
            return;
        }
        const el = document.createElement('script');
        el.src = SRC;
        el.async = true;
        el.defer = true;
        el.setAttribute('data-google-maps', '');
        el.onload = () => resolve(window.google);
        el.onerror = () => {
            // Let a later call retry rather than caching the failure forever.
            promise = null;
            reject(new Error('Google Maps failed to load'));
        };
        document.head.appendChild(el);
    });

    return promise;
};

export default ensureMapsLoaded;
